import * as React from 'react';
import { ClickoutSegment, Category } from '@packages/common';
import { useDyClient } from '@lib/dy';
import { useProductSearchById } from '@packages/services';
import { breakpointSizes, Spacer } from '@packages/ui';

import { ImpressionTracker, WishlistHelperContext, ClickoutProvider, Clickout } from '../../../context';
import { ProductCarousel } from '../../../components/ProductCarousel';
import { deDuplicateProducts, sortAsProvided } from '../utils';

import { RecoTile } from '../../../../../components/RecoTile';

import { useDyRecommendations } from './useDyRecommendations';
import { RecommendationChannel } from '../types';

const breakpoints = Object.values(breakpointSizes).map((breakpoint) => parseInt(breakpoint, 10));

export interface DyRecommendationsProps {
  fallbackTitle?: string;
  isSorted?: boolean;
  service?: 'product' | 'content';
  slidesToShow?: number[];
  recommendationChannel: RecommendationChannel;
  category?: Category;
}

export const DyRecommendations: React.FC<DyRecommendationsProps> = ({
  fallbackTitle = '',
  service = 'product',
  isSorted,
  slidesToShow,
  recommendationChannel,
  category,
}) => {
  const { client } = useDyClient();
  const { wishlistHelper } = React.useContext(WishlistHelperContext);
  const { recommendations, title, decisionId } = useDyRecommendations({
    categoryTitle: category?.title,
    recommendationChannel,
  });

  const orderedProductIds = recommendations.map((slot) => slot.sku);
  const [, , productsResponse] = useProductSearchById(orderedProductIds, 'productId');

  const [products, setProducts] = React.useState(
    sortAsProvided(deDuplicateProducts(productsResponse), orderedProductIds),
  );

  React.useEffect(() => {
    const providedProducts = sortAsProvided(deDuplicateProducts(productsResponse), orderedProductIds);
    if (products.map((p) => p.productId).join(',') !== providedProducts.map((p) => p.productId).join(',')) {
      setProducts(providedProducts);
    }
  }, [orderedProductIds, products, productsResponse]);

  const handleDYRecoClick = (productId: string) => {
    const slotId = recommendations.find((slot) => slot.sku === productId)?.slotId;
    if (slotId) {
      client.engagements({
        engagements: [
          {
            type: 'SLOT_CLICK',
            decisionId,
            slotId,
          },
        ],
      });
    }
  };

  return products.length ? (
    <ClickoutProvider
      recommendationChannel={recommendationChannel}
      segment={ClickoutSegment.RECOMMENDATION}
      service={service}
    >
      <ProductCarousel
        isSorted={isSorted}
        centered={[true, true, false]}
        slidesToShow={slidesToShow}
        breakpoints={breakpoints}
        recommendationChannel={recommendationChannel}
        categoryId={category?._id}
        title={title || fallbackTitle}
        slides={products.map((product, index) => (
          <Spacer p={[2]} key={`product-${index}-${product.objectID}`}>
            <ImpressionTracker product={{ ...product, isDYRecommended: true }} index={index}>
              <div onClick={() => handleDYRecoClick(product.productId)}>
                <RecoTile
                  wishlistHelper={wishlistHelper}
                  key={product.objectID}
                  product={{ ...product, isDYRecommended: true }}
                  Clickout={Clickout}
                  index={index}
                />
              </div>
            </ImpressionTracker>
          </Spacer>
        ))}
      />
    </ClickoutProvider>
  ) : (
    <></>
  );
};
